import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import Card from '@components-clean/core/card';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { InfoField } from '@components-clean/core/info-field';
import { DatasetImportLogDto } from '@interfaces/central-monitoring';
import IHubDataset from '@pages-clean/central-monitoring/dataset-hub/interfaces/hub-dataset.interface';
import { useLocalState } from '@hooks/index';
import { stringDateWithoutTimeFromCurrentUtcDate, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';

type Props = {
    studyRef: string;
    dataset?: IHubDataset;
    cardTitle: React.ReactNode;
};

type ImporterLogRow = {
    logName: string | null;
} & DatasetImportLogDto;

enum ImporterLogType {
    DataLoad = 0,
    CalcFields = 1,
    Forecast = 2,
    ProcessPanels = 3,
}

const getLogName = (logType?: ImporterLogType) => {
    switch (logType) {
        case ImporterLogType.DataLoad:
            return 'logTypes.dataLoad';
        case ImporterLogType.CalcFields:
            return 'logTypes.calcFields';
        case ImporterLogType.Forecast:
            return 'logTypes.forecasted';
        case ImporterLogType.ProcessPanels:
            return 'logTypes.processedData';
        default:
            return null;
    }
};

export const DatasetLogs = ({ studyRef, dataset, cardTitle }: Props) => {
    const { state } = useLocalState();
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('cm', {
        keyPrefix: 'datasetHub.tabs.datasetInfo.chips.datasetLogs',
    });

    const [data, setData] = useState<ImporterLogRow[]>([]);
    const { data: importerLogs, isLoading } = DatasetApi.useGetImporterLogs(studyRef, dataset?.id);
    const { trigger: downloadLog } = DatasetApi.useDownloadImporterLog(studyRef);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (!importerLogs) {
            setData([]);
            return;
        }

        setData(
            importerLogs.map((x) => {
                const logName = getLogName(x.logType);
                return {
                    ...x,
                    logName: logName ? t(logName) : null,
                };
            }),
        );
        /* eslint-enable @eslint-react/set-state-in-effect */
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [importerLogs]);

    const columns: GridColumnProps[] = [
        {
            id: 'logName',
            title: t('grid.columns.logType'),
            field: 'logName',
        },
        {
            id: 'createdDate',
            title: tRoot('metadataColumns.createdDate'),
            field: 'createdDate',
            cells: { data: DateTimeCell('DD-MMM-YYYY HH:mm:ss') },
        },
        {
            id: 'createdBy',
            title: tRoot('metadataColumns.createdBy'),
            field: 'createdBy',
        },
    ];

    const logFileName = (logType?: ImporterLogType) => {
        const studyName = state?.selectedStudy?.shortName;
        const logName = getLogName(logType);
        const translatedLogName = logName ? t(logName) : null;
        const datasetName = stringDateWithoutTimeFromUtc(dataset!.date, 'DD-MMM-YYYY');
        const now = stringDateWithoutTimeFromCurrentUtcDate('DD-MMM-YYYY');
        return `Study ${studyName}_Dataset ${datasetName}_${translatedLogName} Log_${now}.txt`;
    };

    return (
        <Card title={cardTitle} className="k-h-full" padding="none" data-testid="dataset-logs-card">
            {dataset ? (
                <Grid
                    className="k-h-full"
                    title=""
                    testDataId="dataset-logs-grid"
                    columns={columns}
                    data={data}
                    maxHeight="auto"
                    useCardWrapper={false}
                    filterable={false}
                    reorderable={false}
                    sortable={false}
                    rowMenuActions={[
                        {
                            id: 1,
                            label: t('grid.downloadLog'),
                            onClick: (dataItem: unknown) => {
                                const row = dataItem as ImporterLogRow;
                                downloadLog({
                                    logType: row.logType!,
                                    datasetId: dataset!.id,
                                })
                                    .then((result) => {
                                        if (result.blob == null) throw new Error();
                                        saveAs(result.blob, logFileName(row.logType));
                                    })
                                    .catch((e) => window.console.error(e));
                            },
                            icon: ['far', 'download'],
                        },
                    ]}
                    rowMenuActionsWidth={35}
                    loading={isLoading}
                    useLoadingOverlay
                />
            ) : (
                <InfoField type="Info" message={tRoot('cm:datasetHub:selectRecord')} layout="center" />
            )}
        </Card>
    );
};
